import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { BrowserOnly } from 'cccisd-laravel-report';
import Skeleton from 'react-loading-skeleton';
import { Html } from 'cccisd-wysiwyg';
import style from './style.css';

import publicSchoolQuery from './publicSchoolAbsenteeism.graphql';
import privateSchoolQuery from './privateSchoolAbsenteeism.graphql';
import districtQuery from './districtAbsenteeism.graphql';
import stateQuery from './stateAbsenteeism.graphql';

import absenteeismVariables from './absenteeismVariables.js';
import _round from 'lodash/round';
import { useGetOverviewReportData } from 'js/queries';

const Component = props => {
    const reduxStore = useSelector(state => state.app.nasn);
    const deploymentHandle = reduxStore.selectedDeployment.slice(0, 9) + `_${props.widgetProps.reportGroupType}`;

    const getQueryType = () => {
        let query = stateQuery;
        let variables = {
            metricspawns: props.widgetProps.metricspawns,
            deploymentHandle: deploymentHandle.slice(0, 9) + '_state',
            workforce: absenteeismVariables.state,
        };
        const districtVariables = {
            metricspawns: props.widgetProps.metricspawns,
            deploymentHandle: deploymentHandle.slice(0, 9) + '_district',
            workforce: absenteeismVariables.district,
        };
        const schoolVariables = {
            metricspawns: props.widgetProps.metricspawns,
            deploymentHandle: deploymentHandle.slice(0, 9) + '_publicSchool',
            workforce: absenteeismVariables.publicSchool,
        };

        const privateSchoolVariables = {
            metricspawns: props.widgetProps.metricspawns,
            workforce: absenteeismVariables.privateSchool,
            deploymentHandle: deploymentHandle.slice(0, 9) + '_privateSchool',
        };

        if (props.widgetProps.reportGroupType === 'district') {
            query = districtQuery;
            variables = districtVariables;
        }
        if (props.widgetProps.reportGroupType === 'publicSchool') {
            query = publicSchoolQuery;
            variables = schoolVariables;
        }
        if (props.widgetProps.reportGroupType === 'privateSchool') {
            query = privateSchoolQuery;
            variables = privateSchoolVariables;
        }

        return [{ query, variables }];
    };

    const overviewReportQuery = useGetOverviewReportData(
        'absenteeism singleGroup',
        props.widgetProps.reportGroupType,
        getQueryType()
    );

    const queriesLoading = overviewReportQuery.some(q => q.isFetching);

    const getData = () => {
        if (!queriesLoading) {
            const dataReturned = overviewReportQuery.every(q => q.data);
            if (dataReturned) {
                const data = overviewReportQuery[0].data;
                let totalPerMonthAbsent = data.data.roles.metricspawnSummary.totalabsent;
                let totalAnnAbsent = data.data.roles.metricspawnSummary.totalAnnAbsent;
                const totalAbsent = totalPerMonthAbsent + totalAnnAbsent;
                const totalStudents = data.data.roles.metricspawnSummary.totalstudents;
                const per1000 = (totalAbsent / totalStudents) * 1000;

                if (!totalStudents) {
                    return {
                        absenteeism: '?',
                    };
                }
                return { absenteeism: _round(per1000, 2) || '?' };
            }
        }
        return { absenteeism: '?' };
    };

    return (
        <>
            {props.settings.show && (
                <div className={style.wrapper}>
                    <div className={style.header}>
                        <div className={style.headerLeft}>
                            <div className={style.headerIcon} />
                            {props.settings.title || 'Absenteeism'}
                        </div>
                        {!queriesLoading &&
                            overviewReportQuery.data?.data.roles &&
                            !overviewReportQuery.data?.data.roles.metricspawnSummary.totalstudents && (
                                <BrowserOnly>
                                    <div
                                        style={{
                                            fontSize: '0.58em',
                                            color: 'red',
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        Data for this section cannot be calculated without entering total students.
                                    </div>
                                </BrowserOnly>
                            )}
                    </div>

                    <div className={style.content}>
                        {queriesLoading ? (
                            <Skeleton height={84} />
                        ) : (
                            <div className={style.box}>
                                <div className={style.boxIcon} />
                                <div className={style.boxText}>
                                    <div style={{ fontWeight: 'bold' }}>Students chronically absent per month</div>
                                    <div>
                                        <span style={{ fontWeight: 'bold' }}>{getData().absenteeism}</span> / 1000
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className={style.additionalInfo}>
                            <Html content={props.settings.additionalInfo} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

Component.propTypes = {
    settings: PropTypes.object,
    isPreview: PropTypes.bool,
    filters: PropTypes.object,
};

Component.defaultProps = {
    filters: {},
};

export default Component;
